import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useDispatch } from "react-redux";

import { TranscriberRef } from "@/components/Transcriber";
import { useTranscription } from "@/pages/ReportStudio/ReportBuilder/store/useTranscription";
import { showError } from "@/utils/notification";

import type { SpeakerHint } from "../components/SpeakerChecklist";
import {
  adoptRecordingBuffer,
  appendRecordingChunk,
  beginRecordingBuffer,
  cancelRecordingBuffer,
  clearRecordingFinalizeHandler,
  discardRecordingAudio,
  finalizeRecordingBuffer,
  isRecordingBufferCancelled,
  notifyRecordingFinalized,
  resetRecordingBuffer,
  setRecordingFinalizeHandler,
} from "./recordingBuffer";

/**
 * Lifecycle status of an in-panel Scribe recording session (MDRS-116).
 *
 *  - `idle`       — no session; the consent modal is the surface.
 *  - `connecting` — Start Recording clicked, WebSocket handshake in flight.
 *  - `recording`  — live capture + transcription running.
 *  - `paused`     — capture temporarily suspended (no audio to AssemblyAI).
 *  - `stopped`    — session ended, blob assembled, transcript complete.
 */
export type RecordingSessionStatus =
  | "idle"
  | "connecting"
  | "recording"
  | "paused"
  | "stopped";

export type UseRecordingSessionArgs = {
  transcriberRef?: React.RefObject<TranscriberRef | null>;
  /**
   * Fired at the start of a new recording, before the MediaRecorder is
   * constructed, so the owner can drop any stale blob from a prior session.
   */
  onRecordingSessionStart?: () => void;
  /**
   * Fired when the user finalises a session (Done / auto after Stop). Receives
   * the captured audio for the diarized AI summarize path (MDRS-29); null when
   * the session is cancelled/discarded. Also receives the speaker hints this
   * session was started with (MDRS-163 follow-on), snapshotted at the same
   * moment as the blob so the consumer doesn't race a later `start()` call
   * resetting the hook's own `speakerHints` state.
   */
  onRecordingSessionEnd?: (
    audioBlob: Blob | null,
    speakerHints: SpeakerHint[]
  ) => void;
  /**
   * Populated with the discard-and-teardown handler so the page's navigation
   * guard can stop the session on confirm (MDRS-85 nav guard, preserved).
   */
  stopRecordingRef?: React.MutableRefObject<(() => void) | null>;
  /**
   * Resolves the case that owns the session. Recorded on the module-level audio
   * buffer as a diagnostic tag (MDRS-233) — cross-session isolation itself is
   * enforced by session-id ownership, not by this value.
   *
   * A GETTER, not a value: the scribe page claims ownership (`beginSession`) and
   * calls `start()` in the same tick, so a rendered prop would still hold the
   * pre-claim value — always null on the first recording, and the *previous*
   * case's number afterwards. Reading it at claim time is the only correct
   * option, and it keeps `start` referentially stable.
   */
  getOwningCaseNumber?: () => string | null;
};

export type RecordingSession = {
  status: RecordingSessionStatus;
  /** True while `recording` or `paused` — the live view owns the panel. */
  isActive: boolean;
  /** True once stopped — the complete banner + transcript own the panel. */
  isStopped: boolean;
  isConnecting: boolean;
  isPaused: boolean;
  /** MediaRecorder that drives the LiveAudioVisualizer; null when not capturing. */
  mediaRecorder: MediaRecorder | null;
  /** The audio blob assembled once the session stops (MDRS-29). */
  recordedAudioBlob: Blob | null;
  /** Human-readable input device label for the "Input: …" caption. */
  inputDeviceLabel: string;
  /**
   * Ordered role labels for the checked speakers (e.g. ["Examiner",
   * "Examinee", "Interpreter"]), as passed to `start`. Consumed by
   * extendSpeakerRoles's `roleOrder` param so live diarization names
   * speakers by checked role instead of a generic "Speaker C". Empty when
   * no session has started yet.
   */
  roleOrder: string[];
  /**
   * The same checked speakers as `roleOrder`, paired with their role
   * (MDRS-163 follow-on) — e.g. [{role:"Doctor",name:"Dr. Sarah Johnson"}].
   * Exposed here (not just consumed at `start`) so the session-end consumer
   * can read it once the session stops, well after `start` returned, and
   * forward it into the batch summarize payload as `speaker_hints`. Empty
   * when no session has started yet.
   */
  speakerHints: SpeakerHint[];
  /**
   * Begin a session. `maxSpeakers` (>1) enables real-time diarization.
   * `roleOrder` is the checked roles in checklist order — see `roleOrder`
   * above. `speakerHints` is the same checked speakers paired with role —
   * see `speakerHints` above. `caseNumber` is forwarded to the streaming
   * token request so the backend can enforce the recording-consent gate
   * (SCRIBE-02) for the life of the session, including token refreshes.
   */
  start: (
    maxSpeakers: number,
    roleOrder?: string[],
    speakerHints?: SpeakerHint[],
    caseNumber?: string
  ) => Promise<void>;
  pause: () => void;
  resume: () => void;
  /** Stop capture; assembles the blob and moves to `stopped`. */
  stop: () => void;
  /** Discard the session (no blob surfaced) and return to idle. */
  cancel: () => void;
  /** Dismiss the completed (stopped) session view and return to idle. */
  reset: () => void;
};

/**
 * Owns the entire live-recording lifecycle for the Scribe session — the
 * MediaRecorder wiring, pause/stop/cancel handlers, blob assembly, the
 * `isModalSession` store flag, and the navigation-guard teardown ref.
 *
 * Lifted out of AudioRecorder (MDRS-116) so the in-panel ScribeSession can
 * drive the live view while the consent gate stays a modal. AudioRecorder is
 * now consent-only and calls `start()` when the user confirms consent.
 */
export const useRecordingSession = ({
  transcriberRef,
  onRecordingSessionStart,
  onRecordingSessionEnd,
  stopRecordingRef,
  getOwningCaseNumber,
}: UseRecordingSessionArgs): RecordingSession => {
  const dispatch = useDispatch();

  const [status, setStatus] = useState<RecordingSessionStatus>("idle");
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const [recordedAudioBlob, setRecordedAudioBlob] = useState<Blob | null>(null);
  const [inputDeviceLabel, setInputDeviceLabel] = useState("Default microphone");
  const [roleOrder, setRoleOrder] = useState<string[]>([]);
  const [speakerHints, setSpeakerHints] = useState<SpeakerHint[]>([]);

  // Audio chunks live in a module-level buffer, NOT a ref (MDRS-233): a remount
  // of the owning RecordingHost used to destroy the ref and every captured chunk
  // with it. Each `start()` claims the buffer; `sessionIdRef` holds this hook
  // instance's claim so stop/cancel can finalize it even when the instance
  // didn't start the session (re-adoption after a remount, below).
  const sessionIdRef = useRef<string | null>(null);

  /**
   * Wires the buffer-writing listeners onto a MediaRecorder. Used both by
   * `start` and by re-adoption after a remount — a re-adopted recorder's
   * original listeners died with the closure that registered them, so it must be
   * re-wired or its chunks would never reach the buffer.
   */
  const attachRecorderListeners = useCallback(
    (recorder: MediaRecorder, sessionId: string) => {
      recorder.addEventListener("dataavailable", (event) => {
        // Cancellation is read from the BUFFER, not a hook ref: this listener may
        // belong to an instance that has since unmounted, whose ref the live
        // instance cannot reach (MDRS-233 review).
        if (isRecordingBufferCancelled()) return;
        if (event.data) appendRecordingChunk(sessionId, event.data);
      });

      recorder.addEventListener("stop", () => {
        // Assembles from the module buffer and releases it. `owned` is false for
        // a stale recorder whose `stop` landed after a newer session claimed the
        // buffer — that must not be reported as a completion, or it would end the
        // recording still in progress.
        const { owned, blob, cancelled } = finalizeRecordingBuffer(sessionId);
        if (!owned || cancelled) return;
        // Report through the buffer, not directly: this listener belongs to
        // whichever instance called start(), and after a remount that instance's
        // setState is a no-op. The registered handler is always the live one.
        notifyRecordingFinalized(blob);
      });

      recorder.addEventListener("error", (event) => {
        console.error("[useRecordingSession] MediaRecorder error", event);
        showError(
          "Audio capture failed mid-session. The live transcript was preserved but speaker-stamped transcripts won't be available for this recording.",
          dispatch
        );
        // A capture failure is NOT a user cancellation: reusing the cancel flag
        // would suppress finalisation entirely and strand the session in
        // `recording` forever, silently dropping the live transcript the toast
        // above promises was preserved. Discard the (unusable) audio but still
        // finalise, so status reaches `stopped` and the text-only summarize runs.
        discardRecordingAudio();
        notifyRecordingFinalized(null);
      });
    },
    [dispatch]
  );

  // Register THIS instance as the one that receives session completion, so the
  // blob lands on the mounted hook even when the recorder's `stop` listener
  // belongs to an instance that has since unmounted (MDRS-233).
  useEffect(() => {
    const handler = (blob: Blob | null) => {
      if (blob) setRecordedAudioBlob(blob);
      // Flip to stopped only after the blob exists so Done can't fire with a
      // null blob (MDRS-29 review — closes the stop()/event race).
      setStatus("stopped");
    };
    setRecordingFinalizeHandler(handler);
    // Clear only if still ours: a remount registers the new instance's handler
    // before this cleanup could otherwise wipe it.
    return () => clearRecordingFinalizeHandler(handler);
  }, []);

  // Re-adopt an in-flight session on mount. A remount of the owning host creates
  // a fresh hook instance with `status: "idle"` and no MediaRecorder, so without
  // this the live recording is orphaned: its audio sits in the module buffer but
  // `stop()` has nothing to stop and finalises with a null blob — the exact
  // MDRS-233 symptom this hook is meant to prevent. Mount-only by design.
  useEffect(() => {
    const inFlight = adoptRecordingBuffer();
    if (!inFlight) return;
    sessionIdRef.current = inFlight.sessionId;
    if (inFlight.recorder) {
      // Only wire listeners if nobody else already has them on this recorder —
      // they outlive the unmounted component, so re-wiring would double-count.
      if (inFlight.needsListeners) {
        attachRecorderListeners(inFlight.recorder, inFlight.sessionId);
      }
      // Synchronous setState is the point of this effect: syncing React state to
      // an external system (the module-level recorder) that already exists at
      // mount. One extra render at mount, no cascade.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setMediaRecorder(inFlight.recorder);
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setStatus(inFlight.recorder.state === "paused" ? "paused" : "recording");
    }
    // Mount-only: re-adopting on every change would double-wire the listeners.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mark the session as modal/panel-owned for as long as it's active or
  // stopped. Section textareas read this flag and suppress live writes so the
  // streaming partials don't pollute the underlying transcript — the Done flow
  // (summarize) is the only writer.
  const setModalSession = useTranscription((s) => s.setModalSession);

  const isConnecting = status === "connecting";
  const isPaused = status === "paused";
  const isStopped = status === "stopped";
  const isActive = status === "recording" || status === "paused";

  // Keep the store flag in sync with the LIVE portion of a session (connecting /
  // recording / paused) so the section editors suppress live writes while the
  // in-panel ScribeSession owns the view. Once stopped, the panel shows the
  // persisted full-transcript viewer, so the flag clears and search/filter work.
  useEffect(() => {
    const liveSessionOwnsPanel =
      status === "connecting" || status === "recording" || status === "paused";
    setModalSession(liveSessionOwnsPanel);
    return () => setModalSession(false);
  }, [status, setModalSession]);

  const start = useCallback(
    async (
      maxSpeakers: number,
      newRoleOrder: string[] = [],
      newSpeakerHints: SpeakerHint[] = [],
      caseNumber?: string
    ) => {
      if (!transcriberRef?.current) return;

      // Drop any blob from a prior incomplete session so a later fallback POST
      // can't reuse stale audio from a different examinee (MDRS-29 review).
      onRecordingSessionStart?.();

      setRoleOrder(newRoleOrder);
      setSpeakerHints(newSpeakerHints);
      setStatus("connecting");

      const result = await transcriberRef.current.startTranscription({
        ...(maxSpeakers > 1 ? { maxSpeakers } : {}),
        caseNumber,
      });

      if (!result?.mediaStream) {
        // Handshake failed — the Transcriber surfaces its own error. Return to
        // idle so the consent modal can be retried.
        setStatus("idle");
        return;
      }

      // Resolve the input device label for the "Input: …" caption. Falls back
      // to a sensible default when the track has no label (permissions quirk).
      const [audioTrack] = result.mediaStream.getAudioTracks();
      if (audioTrack?.label) {
        setInputDeviceLabel(audioTrack.label);
      }

      let recorder: MediaRecorder;
      try {
        recorder = new MediaRecorder(result.mediaStream);
      } catch (err) {
        // Synchronous throw on browsers that can't negotiate a codec. Tear the
        // live transcription down and return to idle so the user can Cancel /
        // use Upload instead (mirrors the prior AudioRecorder behaviour).
        console.error("[useRecordingSession] MediaRecorder unsupported", err);
        showError(
          "Couldn't start audio capture in this browser. Try Chrome or Firefox, or use the Upload recording flow instead.",
          dispatch
        );
        if (transcriberRef.current?.isTranscribing) {
          transcriberRef.current.stopTranscription();
        }
        setStatus("idle");
        return;
      }

      // Claim the module buffer for this session. Discards anything a prior
      // session left behind, so audio can never cross examinees (MDRS-29).
      const sessionId = beginRecordingBuffer({
        // Read at claim time — the owner is set in the same tick as this call.
        caseNumber: getOwningCaseNumber?.() ?? null,
        // Browser-dependent: Chrome/Firefox return `audio/webm`, Safari may
        // return `audio/mp4`. Tagged onto the assembled blob.
        mimeType: recorder.mimeType || "audio/webm",
        // Published so a hook that mounts mid-session can re-adopt and stop it.
        recorder,
      });
      sessionIdRef.current = sessionId;
      setRecordedAudioBlob(null);

      attachRecorderListeners(recorder, sessionId);

      // 1-second timeslices: bounds data loss on a crash to the last second and
      // keeps the blob-before-stopped invariant robust across browsers.
      recorder.start(1000);
      setMediaRecorder(recorder);
      setStatus("recording");
    },
    [
      transcriberRef,
      onRecordingSessionStart,
      dispatch,
      getOwningCaseNumber,
      attachRecorderListeners,
    ]
  );

  const pause = useCallback(() => {
    if (!transcriberRef?.current) return;
    transcriberRef.current.pauseTranscription();
    if (mediaRecorder && mediaRecorder.state === "recording") {
      mediaRecorder.pause();
    }
    setStatus("paused");
  }, [mediaRecorder, transcriberRef]);

  const resume = useCallback(() => {
    if (!transcriberRef?.current) return;
    transcriberRef.current.resumeTranscription();
    if (mediaRecorder && mediaRecorder.state === "paused") {
      mediaRecorder.resume();
    }
    setStatus("recording");
  }, [mediaRecorder, transcriberRef]);

  const stop = useCallback(() => {
    if (transcriberRef?.current?.isTranscribing) {
      transcriberRef.current.stopTranscription();
    }
    // The `stop` listener flips status to `stopped` once the blob is
    // assembled — do NOT set it here (MDRS-29 review).
    if (mediaRecorder && mediaRecorder.state !== "inactive") {
      mediaRecorder.stop();
    } else {
      // No live recorder (e.g. codec error path, or a recorder that already
      // stopped). The `stop` listener won't fire, so finalise the buffer here —
      // otherwise its chunks are never assembled AND never released, holding
      // Blob memory at module scope until the next start() (MDRS-233 review).
      const sessionId = sessionIdRef.current;
      const { owned, blob, cancelled } = sessionId
        ? finalizeRecordingBuffer(sessionId)
        : { owned: false, blob: null, cancelled: false };
      sessionIdRef.current = null;
      // Mirrors the `stop` listener's bail so neither call site can flip a
      // session it doesn't own. `!owned` means a newer session holds the buffer;
      // leave that session's audio alone and just settle this stale view.
      if (!owned || cancelled) {
        setStatus(cancelled ? "idle" : "stopped");
      } else {
        notifyRecordingFinalized(blob);
      }
    }
    setMediaRecorder(null);
  }, [mediaRecorder, transcriberRef]);

  const cancel = useCallback(() => {
    // Mark cancelled on the BUFFER before stop() so queued events are no-ops for
    // every instance's listeners, not just this one's (MDRS-233 review): after a
    // remount the live recorder's listener belongs to an unmounted instance, and
    // a per-instance flag let a discarded session still dispatch a summarize.
    cancelRecordingBuffer();
    if (transcriberRef?.current?.isTranscribing) {
      transcriberRef.current.stopTranscription();
    }
    if (mediaRecorder && mediaRecorder.state !== "inactive") {
      // Leave the buffer in place: `stop` fires ASYNCHRONOUSLY, and its listener
      // needs to still see `cancelled` to suppress finalisation. It releases the
      // buffer itself via finalizeRecordingBuffer's cancelled branch.
      mediaRecorder.stop();
    } else {
      resetRecordingBuffer();
    }
    sessionIdRef.current = null;
    setRecordedAudioBlob(null);
    setMediaRecorder(null);
    setStatus("idle");
  }, [mediaRecorder, transcriberRef]);

  // Once the session stops, surface the captured audio to the AI summarize path
  // (MDRS-29). There's no explicit "Done" in the in-panel design (MDRS-116) —
  // stopping is the finalisation — so this fires automatically on the
  // recording → stopped transition, exactly once per stop.
  //
  // Fire even when the blob is null: a session can stop with no audio chunks
  // (the `dataavailable`-less / codec-error path) yet still have a live
  // transcript, and the consumer's session-end effect runs a text-only,
  // diarization-less summarize in that case. Guarding on blob identity would
  // skip the null→null case and silently drop that fallback (regression from
  // the old unconditional Done handler). So we guard on the stop transition,
  // not the blob value.
  const finalizedForStopRef = useRef(false);
  useEffect(() => {
    if (status !== "stopped") {
      finalizedForStopRef.current = false;
      return;
    }
    if (finalizedForStopRef.current) return;
    finalizedForStopRef.current = true;
    onRecordingSessionEnd?.(recordedAudioBlob, speakerHints);
  }, [status, recordedAudioBlob, speakerHints, onRecordingSessionEnd]);

  const reset = useCallback(() => {
    setRecordedAudioBlob(null);
    setStatus("idle");
  }, []);

  // Expose the discard-and-teardown handler while a session is live so the
  // page's navigation guard can stop it on confirm (MDRS-85). Cleared when
  // idle so leaving the page is unguarded once nothing is recording.
  useEffect(() => {
    if (!stopRecordingRef) return;
    stopRecordingRef.current = isActive || isStopped ? cancel : null;
    return () => {
      stopRecordingRef.current = null;
    };
  }, [stopRecordingRef, isActive, isStopped, cancel]);

  return useMemo(
    () => ({
      status,
      isActive,
      isStopped,
      isConnecting,
      isPaused,
      mediaRecorder,
      recordedAudioBlob,
      inputDeviceLabel,
      roleOrder,
      speakerHints,
      start,
      pause,
      resume,
      stop,
      cancel,
      reset,
    }),
    [
      status,
      isActive,
      isStopped,
      isConnecting,
      isPaused,
      mediaRecorder,
      recordedAudioBlob,
      inputDeviceLabel,
      roleOrder,
      speakerHints,
      start,
      pause,
      resume,
      stop,
      cancel,
      reset,
    ]
  );
};
